/* eslint-disable react/no-unstable-nested-components */
import React, {
  useContext, useMemo, useState, useRef,
} from 'react';
import Btn, { BtnModifier } from 'components/btn';
import { StatusEnum, TableWrapperProps } from 'models/general';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StoreContext } from 'models/import';
import PlatformCard, { PlatformCardModifier } from 'components/platformCard';
import Tooltip, { TooltipAlignEnum, TooltipCursorEnum, TooltipModifier } from 'components/tooltip';
import { MaterialReactTable, useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
} from 'material-react-table';
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query';
import { getLaminasApiData } from 'services/api';
import { ImportTableApiJob, ImportTableBalanceStatus } from './importTable';
import { TableDataRes } from './base/tableBaseApi';
import StatusIcon, { StatusIconModifier } from 'components/statusIcon';
import { OverviewSideTableProps } from 'pages/overview/overviewSide';
import { getStripedRowsForTable } from 'services/util';
import { getDateForUI } from 'services/transactions';

export interface OverviewRightTableJob {
  apiKeyOrWalletAddress: string;
  errorCode: number;
  errorMessage: string;
}

export interface OverviewRightTableDef {
  customApiUri: string | null;
  displayName: string;
  ticker: null|string;
  errorCode: number;
  errorMessage: string;
  lastImportTimestamp: number;
  customName: string;
  lastUpdated: number;
  jobs: OverviewRightTableJob[] | null;
  logoUri: string | null;
  jobType: string;
}

function OverviewRightTable(props: OverviewSideTableProps) {
  const tableRef = useRef();

  const location = useLocation();
  const { t } = useTranslation();
  const { userSettings, importTableSettings, setImportTableSettings } = useContext(StoreContext);
  const cachedDataKeyValue = 'overview_right_table';

  const onNavigate = (url: string | null) => {
    if (url && url.length) {
      window.location.href = url;
    }
  };

  const {
    data, isError, isFetching, isLoading, refetch,
  } = useQuery<TableDataRes<OverviewRightTableDef, any>>({
    queryKey: [{
      filter: props.filters, // additional external filters
      userId: userSettings?.userId,
      tableData: props.data,
    },
    ],
    queryFn: async (item) => {
      if (props.filters === '' || props.filters === '{}') { return null; }

      //cached data on returning from detail
      if (!props.data) {
        const val = window.localStorage.getItem(cachedDataKeyValue);
        if (val) {
          window.localStorage.removeItem(cachedDataKeyValue);
          return JSON.parse(val);
        }
      }

      const baseUri = '/tx-overview-v2/import-job-overview';
      const filterParameters = props.filters ? props.filters : '';
      const requestUri = `${baseUri}?${filterParameters}`;

      const response = await getLaminasApiData<TableDataRes<OverviewRightTableDef, any>>(requestUri);
      props.onRightTableLoaded(response);
      return response;
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: data?.meta?.total_item_count || 50,//Needs to be the maximum value we can have or it will always show up to this.
  });

  const columns = useMemo<MRT_ColumnDef<OverviewRightTableDef>[]>(() => [
    {
      accessorKey: 'displayName',
      minSize: 170,
      size: 170,
      maxSize: 170,
      header: '',
      Header: ({ column }) => (
        <>
         {t('Recently Used (API)', { ns: 'overview' })}
        <Tooltip
        cursor={TooltipCursorEnum.POINTER}
          align={TooltipAlignEnum.LEFT}
          message= {t('API Live Data', { ns: 'overview' })}
          modifiers={[]}
          key={column.id}
        >
          <a href="/import/balance/">
            <img src="/img/open.png" className='ct_overviewHeader_importBalance' />
          </a>
        </Tooltip>
        </>
      ),
      Cell: (info) => {
        const displayName: string = info.cell.getValue() as string | '';
        const { ticker, logoUri, jobs } = info.row.original;
        const jobCount = (jobs || []).length
        const jobCountString = jobCount > 1 ? ` (${jobCount})` : '';

        if (!displayName) {
          return <p />;
        }

        const combinedDisplayName : string = ticker ? `${displayName} (${ticker})${jobCountString}` : `${displayName}${jobCountString}`;

        return (
          <PlatformCard
            logo_uri={logoUri}
            name={displayName}
            display_name={combinedDisplayName}
            beta={false}
            modifiers={[PlatformCardModifier.OLD, PlatformCardModifier.OVERVIEW, PlatformCardModifier.SMALL_HEIGHT]}
          />
        );
      },
    },
    {
      accessorKey: 'lastImportTimestamp',
      minSize: 50,
      header: t('Last Import', { ns: 'overview' }),
      accessorFn: (row) => getDateForUI(row.lastImportTimestamp, userSettings?.dateFormat),
      Cell: ({ cell }) => (
        <div
          className="ct_tableBase-text-wrap-one-line"
          style={{ maxWidth: '110px' }}
        >
          {cell.getValue<string>() || ''}

        </div>
      ),
    },
    {
      accessorKey: 'customApiUri',
      header: t('Status', { ns: 'overview' }),
      size: 15,
      minSize: 15,
      maxSize: 15,
      Cell: (info) => {
        const apiUri: string = info.cell.getValue() as string || '';
        if (!apiUri) return '';

        const errorCodeArray: OverviewRightTableJob[] = info.row.original.jobs || [];
        const errorCodeCount: number = errorCodeArray.map((e) => e.errorCode).filter(e => e>0).length;
        const singOrPlural = (errorCodeCount > 1) ? t('Errors', { ns: 'overview' }) : t('Error', { ns: 'overview' });
        const errorString = `${errorCodeCount} API ${singOrPlural}`;

        if (!(errorCodeCount > 0)) return '';

        return (
          <>
           {errorCodeCount > 0 && (
          <Tooltip
            align={TooltipAlignEnum.RIGHT}
            cursor={TooltipCursorEnum.HELP}
            modifiers={[TooltipModifier.OVERVIEW]}
            tooltipChildren={(
              <p>{errorString}</p>
            )}
          >
            <StatusIcon type={StatusEnum.WARNING} modifiers={[StatusIconModifier.OVERVIEW]} />
          </Tooltip>
           )}
           </>
        );
      },
    },
  ], [props.currentMismatch, props.userCurrencyMismatch]);

  return (
    <>
    <MaterialReactTable
      tableInstanceRef={tableRef}
      columns={columns}
      data={data?.data ?? []}
      initialState={{
        showColumnFilters: false,
        density: 'compact',
        columnVisibility: {
          'mrt-row-expand': false,
          detail: false,
        },
      }}
      uiTableProps={{
        sx: {
          tableLayout: 'fixed',
        },
      }}
      muiTableBodyRowProps={({ row }) => ({
        hover: true,
        onClick: (event) => {
          if (isFetching || props.showSpinners) return;
          event.stopPropagation();
          onNavigate(row.original.customApiUri);
        },
        sx: { cursor: (isFetching || props.showSpinners) ? 'not-allowed' : 'pointer' },
      })}
      muiTableHeadCellProps={({column}) => ({
        sx: {
          padding: '4px',
        },
      })}
      muiTableBodyCellProps={({cell}) => ({
        sx: {
          padding: '4px',
        },
      })}
      muiTableBodyProps={({ row }) => ({ 
        sx: getStripedRowsForTable()
      })}
      muiToolbarAlertBannerProps={
      isError
        ? {
          color: 'error',
          children: t('Error loading data'),
        }
        : undefined
    }
      enableColumnActions={false}
      enableColumnFilters={false}
      enablePagination={false}
      enableSorting={false}
      enableBottomToolbar={false}
      enableTopToolbar={false}
      defaultColumn={{
        size: 20,
        columnFilterModeOptions: [
          'fuzzy',
        ],
        enableColumnFilter: false,
        enableColumnFilterModes: false,
      }}
      state={{
        globalFilter: props.globalFilter,
        isLoading,
        showAlertBanner: isError,
        showProgressBars: false,
        showSkeletons: isFetching,
        showGlobalFilter: true,
        pagination,
      }}
      muiFilterTextFieldProps={{
        sx: { m: '0.5rem 0', width: '100%' },
      }}
      />
    </>
  );
}

const queryClient = new QueryClient();

function OverviewRightTableWithReactQueryProvider(props: OverviewSideTableProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <OverviewRightTable {...props} />
    </QueryClientProvider>
  );
}

export default OverviewRightTableWithReactQueryProvider;
